/**
 * Custom Dropdown Component
 * Prevents white flash issue with native select elements
 * Extracted from AuditLog to eliminate duplication
 */

import React, { useState, useRef, useEffect } from 'react';

/**
 * Custom Dropdown Component
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Current selected value
 * @param {Array} props.options - Array of options { value, label }
 * @param {Function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {Object} props.style - Additional styles
 */
const CustomDropdown = ({
    value,
    options = [],
    onChange,
    placeholder = 'Select...',
    style = {}
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayLabel = selectedOption ? selectedOption.label : placeholder;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const styles = {
        container: {
            position: 'relative',
            ...style
        },
        trigger: {
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.625rem 0.75rem',
            backgroundColor: 'var(--bg)',
            border: '1px solid #475569',
            borderRadius: '6px',
            color: 'var(--fg-primary)',
            fontSize: '0.875rem',
            cursor: 'pointer',
            outline: 'none',
            textAlign: 'left',
            transition: 'background-color 0.15s'
        },
        arrow: {
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            fontSize: '0.75rem',
            color: '#94a3b8'
        },
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
        },
        menu: {
            position: 'absolute',
            top: 'calc(100% + 0.25rem)',
            left: 0,
            right: 0,
            background: 'var(--bg)',
            border: '1px solid #475569',
            borderRadius: '6px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.4)',
            zIndex: 1000,
            overflow: 'hidden',
            maxHeight: '300px',
            overflowY: 'auto'
        },
        option: {
            padding: '0.625rem 0.75rem',
            fontSize: '0.875rem',
            cursor: 'pointer',
            transition: 'background-color 0.15s'
        }
    };

    const handleOptionClick = (optionValue) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    return (
        <div ref={dropdownRef} style={styles.container}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                style={styles.trigger}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--border)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--bg)'}
            >
                <span>{displayLabel}</span>
                <span style={styles.arrow}>▼</span>
            </button>

            {isOpen && (
                <>
                    <div style={styles.backdrop} onClick={() => setIsOpen(false)} />
                    <div style={styles.menu}>
                        {options.map((option) => (
                            <div
                                key={option.value}
                                onClick={() => handleOptionClick(option.value)}
                                style={{
                                    ...styles.option,
                                    color: value === option.value ? '#f8fafc' : 'var(--fg-primary)',
                                    background: value === option.value ? 'var(--border)' : 'transparent',
                                    fontWeight: value === option.value ? '600' : 'normal'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'var(--border)'}
                                onMouseLeave={(e) => e.target.style.background =
                                    value === option.value ? 'var(--border)' : 'transparent'}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default CustomDropdown;

